<script setup>
import { computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  status: {
    type: String,
    default: 'draft',
  },
  documentsCount: {
    type: Number,
    default: 0,
  },
  webpagesCount: {
    type: Number,
    default: 0,
  },
  faqsCount: {
    type: Number,
    default: 0,
  },
  assistantsCount: {
    type: Number,
    default: 0,
  },
  updatedAt: {
    type: [String, Number],
    default: 0,
  },
  logoUrl: {
    type: String,
    default: '',
  },
  creator: {
    type: Object,
    default: () => ({ name: 'Unknown' }),
  },
  openMenuId: {
    type: Number,
    default: null,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  showMenu: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'action',
  'menu-open',
  'menu-close',
  'select',
  'hover',
]);

// 将 ISO 时间字符串转换为 Unix 时间戳（秒）
const toUnixTimestamp = time => {
  if (!time) return 0;
  // 如果已经是数字（时间戳），直接返回
  if (typeof time === 'number') return time;
  // 如果是字符串，转换为时间戳
  return Math.floor(new Date(time).getTime() / 1000);
};

// Checkbox 双向绑定
const modelValue = computed({
  get: () => props.isSelected,
  set: value => emit('select', value),
});
const { checkPermissions } = usePolicy();
const { t } = useI18n();
const router = useRouter();
const store = useStore();

// 下拉菜单展开方向：'down' 或 'up'
const dropdownDirection = ref('down');
const triggerRef = ref(null);

// 根据父组件传入的 openMenuId 判断当前菜单是否打开
const showActionsDropdown = computed(() => props.openMenuId === props.id);

// 检测下拉菜单应该往上还是往下展开
const checkDropdownDirection = () => {
  nextTick(() => {
    const trigger = triggerRef.value;
    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const dropdownHeight = 200; // 估算下拉菜单高度

    // 如果下方空间不足（小于菜单高度 + 20px 缓冲），就往上展开
    const spaceBelow = windowHeight - triggerRect.bottom;
    dropdownDirection.value = spaceBelow < dropdownHeight + 20 ? 'up' : 'down';
  });
};

// 切换下拉菜单
const toggleDropdown = () => {
  if (showActionsDropdown.value) {
    // 关闭菜单
    emit('menu-close');
  } else {
    // 打开菜单
    emit('menu-open', props.id);
    // 打开后检测方向
    checkDropdownDirection();
  }
};

// 菜单项配置
const menuItems = computed(() => {
  const allOptions = [
    {
      label: t('PILOT.KNOWLEDGE_BASES.OPTIONS.VIEW'),
      value: 'view',
      action: 'view',
      icon: 'i-lucide-eye',
    },
  ];

  if (checkPermissions(['administrator'])) {
    // 发布/下架选项（根据状态显示）
    if (props.status === 'draft') {
      allOptions.push({
        label: t('PILOT.KNOWLEDGE_BASES.OPTIONS.PUBLISH'),
        value: 'publish',
        action: 'publish',
        icon: 'i-lucide-upload-cloud',
      });
    } else {
      allOptions.push({
        label: t('PILOT.KNOWLEDGE_BASES.OPTIONS.UNPUBLISH'),
        value: 'unpublish',
        action: 'unpublish',
        icon: 'i-lucide-download-cloud',
      });
    }

    allOptions.push(
      {
        label: t('PILOT.KNOWLEDGE_BASES.OPTIONS.EDIT'),
        value: 'edit',
        action: 'edit',
        icon: 'i-lucide-pencil-line',
      },
      {
        label: t('PILOT.KNOWLEDGE_BASES.OPTIONS.DELETE'),
        value: 'delete',
        action: 'delete',
        icon: 'i-lucide-trash',
      }
    );
  }

  return allOptions;
});

const handleAction = ({ action, value }) => {
  // 关闭菜单
  emit('menu-close');

  // 查看操作直接跳转，不触发 emit
  if (action === 'view') {
    router.push({
      name: 'pilot_knowledge_bases_documents',
      params: {
        id: props.id,
      },
    });
    return;
  }

  emit('action', { action, value, id: props.id });
};

// 卡片点击处理
const handleCardClick = () => {
  router.push({
    name: 'pilot_knowledge_bases_documents',
    params: {
      id: props.id,
    },
  });
};

const hasDescription = computed(
  () => !!props.description && props.description.length > 0
);

const hasLogo = computed(() => !!props.logoUrl);

const documentsCountLabel = computed(() => {
  return props.documentsCount === 1
    ? t('PILOT.KNOWLEDGE_BASES.DOCUMENTS_COUNT.SINGLE')
    : t('PILOT.KNOWLEDGE_BASES.DOCUMENTS_COUNT.MULTIPLE', {
        count: props.documentsCount,
      });
});

const assistantsCountLabel = computed(() => {
  return props.assistantsCount === 1
    ? t('PILOT.KNOWLEDGE_BASES.ASSISTANTS_COUNT.SINGLE')
    : t('PILOT.KNOWLEDGE_BASES.ASSISTANTS_COUNT.MULTIPLE', {
        count: props.assistantsCount,
      });
});

const getInitial = name => {
  return name.charAt(0).toUpperCase();
};

const getColor = name => {
  const colors = [
    '#8B5CF6',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#EC4899',
    '#6366F1',
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const getCreatorColor = name => {
  const colors = [
    '#8B5CF6',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#EC4899',
    '#6366F1',
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const timeLabel = computed(() => {
  const timestamp = toUnixTimestamp(props.updatedAt);
  return `${t('PILOT.KNOWLEDGE_BASES.UPDATED')} ${dynamicTime(timestamp)}`;
});

// 内联编辑名称相关
const isEditingName = ref(false);
const editingName = ref('');
const nameInputRef = ref(null);

// 开始编辑名称
const startEditingName = () => {
  editingName.value = props.name;
  isEditingName.value = true;
  nextTick(() => {
    if (nameInputRef.value) {
      nameInputRef.value.focus();
      nameInputRef.value.select();
    }
  });
};

// 取消编辑名称
const cancelEditingName = () => {
  isEditingName.value = false;
  editingName.value = '';
};

// 保存名称
const saveName = async () => {
  if (!editingName.value.trim()) {
    cancelEditingName();
    return;
  }

  if (editingName.value !== props.name) {
    try {
      await store.dispatch('pilotKnowledgeBases/update', {
        id: props.id,
        data: { name: editingName.value.trim() },
      });
      useAlert(t('PILOT.KNOWLEDGE_BASES.EDIT.SUCCESS_MESSAGE'));
    } catch (error) {
      const errorMessage =
        error?.message || t('PILOT.KNOWLEDGE_BASES.EDIT.ERROR_MESSAGE');
      useAlert(errorMessage);
      cancelEditingName();
      return;
    }
  }

  // 等待一下确保状态更新完成
  await nextTick();
  isEditingName.value = false;
};

// 处理按键事件
const handleNameKeydown = event => {
  if (event.key === 'Enter') {
    saveName();
  } else if (event.key === 'Escape') {
    cancelEditingName();
  }
};

// 状态标签相关
const statusConfig = computed(() => {
  const configs = {
    draft: {
      label: t('PILOT.KNOWLEDGE_BASES.STATUS.DRAFT'),
      bgColor: 'bg-n-amber-1 dark:bg-n-amber-2',
      textColor: 'text-n-amber-10 dark:text-n-amber-9',
      dotColor: 'bg-n-amber-5',
    },
    published: {
      label: t('PILOT.KNOWLEDGE_BASES.STATUS.PUBLISHED'),
      bgColor: 'bg-n-blue-1 dark:bg-n-blue-2',
      textColor: 'text-n-blue-10 dark:text-n-blue-9',
      dotColor: 'bg-n-blue-5',
    },
  };
  return configs[props.status] || configs.draft;
});
</script>

<template>
  <div
    class="relative"
    :class="{ 'z-50': showActionsDropdown }"
    @mouseenter="emit('hover', true)"
    @mouseleave="emit('hover', false)"
  >
    <div
      class="group bg-n-slate-white dark:bg-n-slate-800 rounded-xl cursor-pointer"
      style="min-height: 200px"
      @click="handleCardClick"
    >
      <!-- Checkbox (批量选择) -->
      <div
        v-show="selectable"
        class="absolute top-4 ltr:left-3 rtl:right-3 z-10"
      >
        <div class="scale-150 origin-left">
          <Checkbox v-model="modelValue" @click.stop />
        </div>
      </div>
      <div class="flex flex-col h-full">
        <!-- 上半部分：logo、描述、状态 - 独立的圆角卡片 -->
        <div
          class="relative bg-[#F4F5F6] group-hover:bg-[#E3E5E8] rounded-xl p-4 transition-colors duration-200"
        >
          <!-- 操作按钮（右上角绝对定位） -->
          <div
            v-if="showMenu"
            ref="triggerRef"
            v-on-clickaway="() => emit('menu-close')"
            class="absolute top-3 right-3 flex items-center"
            @click.stop
          >
            <Button
              icon="i-lucide-ellipsis-vertical"
              color="slate"
              size="sm"
              class="rounded-md opacity-0 group-hover:opacity-100 transition-opacity !bg-white dark:!bg-n-slate-700 shadow-sm hover:!bg-n-alpha-3"
              @click="toggleDropdown()"
            />
            <DropdownMenu
              v-if="showActionsDropdown"
              :menu-items="menuItems"
              class="ltr:right-0 rtl:left-0"
              :class="[
                dropdownDirection === 'up'
                  ? 'bottom-full mb-1'
                  : 'top-full mt-1',
              ]"
              @action="handleAction($event)"
            />
          </div>

          <!-- Top Section: Description (Left) + Logo (Right) -->
          <div class="flex items-start gap-4">
            <!-- Left: Description Container -->
            <div class="flex-1 min-w-0">
              <p
                v-if="hasDescription"
                class="text-sm text-n-slate-11 line-clamp-2 break-words overflow-hidden"
              >
                {{ description }}
              </p>
              <p v-else class="text-sm text-n-slate-9 italic">
                {{ t('PILOT.KNOWLEDGE_BASES.NO_DESCRIPTION') }}
              </p>
            </div>

            <!-- Right: Logo Container -->
            <div class="flex-shrink-0">
              <img
                v-if="hasLogo"
                :src="logoUrl"
                :alt="name"
                class="w-14 h-14 rounded-lg object-cover"
              />
              <div
                v-else
                class="w-14 h-14 rounded-lg flex items-center justify-center"
                :style="{ backgroundColor: getColor(name) }"
              >
                <span class="text-xl font-semibold" style="color: #ffffff">
                  {{ getInitial(name) }}
                </span>
              </div>
            </div>
          </div>

          <!-- 状态标签 -->
          <div class="mt-2">
            <div
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
              :class="[statusConfig.bgColor, statusConfig.textColor]"
            >
              <span
                class="w-1.5 h-1.5 rounded-full"
                :class="[statusConfig.dotColor]"
              />
              <span>{{ statusConfig.label }}</span>
            </div>
          </div>
        </div>

        <!-- 底部区域：名称、创建人、时间、统计信息 + 操作按钮 -->
        <div
          class="flex flex-col px-0 pb-0 pt-1 transition-all duration-200 relative"
        >
          <!-- 第一行：名称 -->
          <div class="group/name">
            <!-- 默认显示：名称 + 编辑图标 -->
            <div v-if="!isEditingName" class="flex items-center gap-1">
              <h3
                class="text-[14px] font-semibold text-n-slate-12 truncate cursor-pointer hover:text-n-slate-11"
                @click.stop="startEditingName"
              >
                {{ name }}
              </h3>
              <!-- 编辑图标 - hover时显示 -->
              <button
                v-if="checkPermissions(['administrator']) && showMenu"
                class="opacity-0 group-hover/name:opacity-100 transition-opacity flex-shrink-0"
                @click.stop="startEditingName"
              >
                <div
                  class="i-lucide-pencil text-[14px] text-n-slate-10 hover:text-n-slate-12"
                />
              </button>
            </div>

            <!-- 编辑模式：输入框 -->
            <div v-else class="flex items-center gap-1" @click.stop>
              <input
                ref="nameInputRef"
                v-model="editingName"
                type="text"
                class="w-full text-[14px] font-semibold text-n-slate-12 bg-white dark:bg-n-slate-700 border border-n-blue-5 rounded px-2 py-1 outline-none focus:border-n-blue-6"
                @keydown="handleNameKeydown"
                @blur="saveName"
              />
            </div>
          </div>

          <!-- 第二行：创建人 + 更新时间 -->
          <div class="flex items-center justify-between">
            <!-- Left: 创建人圆圈 + 名称 -->
            <div class="flex items-center gap-2">
              <div
                class="w-4 h-4 rounded-full flex items-center justify-center"
                :style="{
                  backgroundColor: getCreatorColor(creator?.name || 'Unknown'),
                }"
              >
                <span class="text-[10px] font-semibold" style="color: #ffffff">
                  {{ getInitial(creator?.name || 'Unknown') }}
                </span>
              </div>
              <span class="text-xs text-n-slate-10">
                {{ creator?.name || 'Unknown' }}
              </span>
            </div>

            <!-- Right: 更新时间 -->
            <div>
              <span class="text-xs text-n-slate-10">
                {{ timeLabel }}
              </span>
            </div>
          </div>

          <!-- 第二行：统计信息（文档数 + 网页数 + FAQ数 + 助手数）-->
          <div class="flex flex-col gap-1">
            <!-- 内容统计：文档 + 网页 + FAQ -->
            <div class="flex items-center gap-3 text-xs text-n-slate-11">
              <!-- 文档数量 -->
              <div class="flex items-center gap-1">
                <i class="i-lucide-file-text text-sm" />
                <span>{{ documentsCount }}</span>
              </div>
              <!-- 网页数量 -->
              <div class="flex items-center gap-1">
                <i class="i-lucide-globe text-sm" />
                <span>{{ webpagesCount }}</span>
              </div>
              <!-- FAQ 数量 -->
              <div class="flex items-center gap-1">
                <i class="i-lucide-message-circle-question text-sm" />
                <span>{{ faqsCount }}</span>
              </div>
            </div>

            <!-- 助手数量 -->
            <div class="flex items-center gap-1.5 text-xs text-n-slate-11">
              <i class="i-lucide-bot text-sm" />
              <span>{{ assistantsCountLabel }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
